/**
 * Rudras AI — Internal Admin Portal
 *
 * Architecturally separate from the customer portal:
 *   - Auth: hits POST /api/auth/login with portal="internal" (backend rejects business_admin).
 *   - Data: hits /api/internal-admin/* endpoints (require role in INTERNAL_ROLES).
 *   - Routing: lives entirely under /internal-admin/* so there's zero overlap with /dashboard.
 *
 * NO public signup link is exposed here. Operators are provisioned by super_admin only.
 * UI reuses existing shadcn components — no new design system.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { formatApiError } from "../lib/errors";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Sparkles, ShieldCheck, Users, Mail, Building2, LayoutDashboard, LogOut, Loader2, RefreshCw, AlertCircle, CheckCircle2, XCircle, KeyRound, Brain, Flame, Rocket, Activity, MessageSquare, StickyNote, Trash2, ListChecks, UserCog, Truck, ShieldAlert, Image as ImageIcon } from "lucide-react";
import RudrasLogo from "../components/RudrasLogo";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { ChangePasswordCard } from "./Dashboard";

const INTERNAL_ROLES = new Set(["super_admin", "sales_manager", "sales_rep", "support", "analyst"]);

export function isInternalRole(role) {
  return INTERNAL_ROLES.has(role);
}

// ---------- Login page (internal-only, NO signup link) ----------
export function InternalLoginPage() {
  const { login, user, logout } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  // Stabilization phase (Feb 2026): backend-asleep detection.
  // Probe /api/health on mount. If the preview pod was hibernated by
  // Emergent's auto-sleep, the call 404/502/503/504/ERR_NETWORKs — we
  // surface a clear banner BEFORE the user types credentials so they
  // don't waste time chasing a non-existent auth bug.
  const [backendStatus, setBackendStatus] = useState("checking"); // checking | ok | asleep

  useEffect(() => {
    if (user && !isInternalRole(user.role)) {
      try { logout(); } catch (_e) { /* no-op */ }
      try { localStorage.removeItem("ren_token"); } catch (_e) { /* no-op */ }
    }
    let alive = true;
    api.get("/health", { timeout: 6000 })
      .then(() => { if (alive) setBackendStatus("ok"); })
      .catch((err) => {
        if (!alive) return;
        const s = err?.response?.status;
        if (s === 404 || s === 502 || s === 503 || s === 504 || err.code === "ERR_NETWORK") {
          setBackendStatus("asleep");
        } else {
          // Some other 4xx/5xx — backend is up enough to respond; assume OK.
          setBackendStatus("ok");
        }
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (user && isInternalRole(user.role)) return <Navigate to="/internal-admin" replace/>;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // PERMANENT FIX — strip whitespace + normalize email casing so a
    // copy-paste from /app/memory/test_credentials.md (which sometimes
    // picks up trailing spaces or non-breaking spaces) doesn't fail.
    const emailNorm = (form.email || "").trim().toLowerCase();
    const passwordNorm = (form.password || "").trim();
    try {
      const u = await login(emailNorm, passwordNorm, "internal");
      if (!isInternalRole(u.role)) {
        toast.error("This account is not authorised for the internal admin portal.");
        return;
      }
      toast.success(`Welcome, ${u.full_name}`);
      nav("/internal-admin");
    } catch (err) {
      // Disambiguate "credentials wrong" (401) from "backend asleep / route
      // missing" (404, 502, 503, 504, network error) so the user gets an
      // actionable message instead of staring at "login failed" when the
      // preview pod was simply hibernated by the platform.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 404 || status === 502 || status === 503 || status === 504 || err.code === "ERR_NETWORK") {
        toast.error(
          "Backend is unreachable. If you're on the preview environment, click 'Wake up servers' in the bottom banner. Otherwise contact Emergent Support.",
          { duration: 9000 },
        );
      } else if (status === 401) {
        toast.error("Login failed — please check your email and password.");
      } else {
        toast.error(formatApiError(err, "Login failed — please try again."));
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="internal-login-page">
      <div className="hidden lg:block bg-primary p-12 text-primary-foreground grain-bg relative">
        <Link to="/" className="flex items-center gap-2">
          <RudrasLogo size="md" />
          <span className="font-display font-semibold text-xl">Rudras-AI · Internal</span>
        </Link>
        <div className="absolute bottom-12 left-12 right-12">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider bg-accent/20 px-3 py-1 rounded-full mb-4"><ShieldCheck className="w-3.5 h-3.5"/>Restricted access</div>
          <h2 className="font-display text-4xl font-semibold mb-3">Internal admin portal.</h2>
          <p className="text-primary-foreground/70 max-w-md">For Rudras-AI team members only. Operators are provisioned by a super admin — there is no self-service signup here.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="internal-login-form">
          <div>
            <h1 className="font-display text-3xl font-semibold">Internal admin</h1>
            <p className="text-muted-foreground text-sm mt-1">Rudras-AI staff sign-in. <Link to="/login" className="text-accent hover:underline" data-testid="link-customer-login">Customer login →</Link></p>
          </div>
          {backendStatus === "asleep" && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 flex items-start gap-2"
              data-testid="internal-login-backend-asleep"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
              <div>
                <div className="font-medium">Preview backend is asleep</div>
                <div className="mt-0.5">
                  Click <span className="font-mono">"Wake up servers"</span> in the bottom banner before signing in. Login will return 404 until the backend pod is awake.
                </div>
              </div>
            </div>
          )}
          <div>
            <Label>Email</Label>
            <Input type="email" required data-testid="internal-login-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-1.5"/>
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" required data-testid="internal-login-password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="mt-1.5"/>
          </div>
          <Button type="submit" disabled={loading} data-testid="internal-login-submit" className="w-full bg-primary hover:bg-primary/90">{loading ? "Signing in..." : "Sign in"}</Button>
          <div className="text-xs text-muted-foreground">
            <Link to="/internal-admin/forgot-password" className="hover:text-accent hover:underline" data-testid="internal-link-forgot-password">Forgot password?</Link>
          </div>
        </form>
      </div>
    </div>
  );
}


// ---------- Internal-admin forgot password ----------
export function InternalForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim(), portal: "internal" });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err, "Could not send reset link. Please try again."));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="internal-forgot-password-page">
      <div className="hidden lg:block bg-primary p-12 text-primary-foreground grain-bg relative">
        <Link to="/" className="flex items-center gap-2">
          <RudrasLogo size="md" />
          <span className="font-display font-semibold text-xl">Rudras-AI · Internal</span>
        </Link>
        <div className="absolute bottom-12 left-12 right-12">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider bg-accent/20 px-3 py-1 rounded-full mb-4"><ShieldCheck className="w-3.5 h-3.5"/>Restricted access</div>
          <h2 className="font-display text-4xl font-semibold mb-3">Forgot your operator password?</h2>
          <p className="text-primary-foreground/70 max-w-md">We'll email you a single-use reset link valid for 30 minutes. Customer accounts cannot use this flow — request a customer reset instead.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-5">
          <h1 className="font-display text-3xl font-semibold">Reset internal password</h1>
          <p className="text-muted-foreground text-sm">Enter the email tied to your Rudras-AI operator account.</p>
          {!submitted ? (
            <form onSubmit={submit} className="space-y-4" data-testid="internal-forgot-password-form">
              <div>
                <Label>Email</Label>
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="internal-forgot-email"/>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90" data-testid="internal-forgot-submit">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          ) : (
            <div className="rounded-md border bg-muted/40 p-4 text-sm" data-testid="internal-forgot-submitted">
              <div className="font-medium mb-1">Check your inbox.</div>
              <div className="text-muted-foreground">If an account exists, a reset link has been sent. The link expires in 30 minutes.</div>
              <div className="text-xs text-muted-foreground mt-3 border-t pt-3">Didn't receive it? Check spam, or contact your super admin.</div>
            </div>
          )}
          <div className="text-sm"><Link to="/internal-admin/login" className="text-accent hover:underline">← Back to internal login</Link></div>
        </div>
      </div>
    </div>
  );
}


// ---------- Internal-admin reset password ----------
export function InternalResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Missing reset token — please use the link from your email.");
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (pwd !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd, portal: "internal" });
      setDone(true);
      toast.success("Password updated. You can sign in now.");
      setTimeout(() => navigate("/internal-admin/login", { replace: true }), 1500);
    } catch (err) {
      toast.error(formatApiError(err, "Could not reset password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="internal-reset-password-page">
      <div className="hidden lg:block bg-primary p-12 text-primary-foreground grain-bg relative">
        <Link to="/" className="flex items-center gap-2">
          <RudrasLogo size="md" />
          <span className="font-display font-semibold text-xl">Rudras-AI · Internal</span>
        </Link>
        <div className="absolute bottom-12 left-12 right-12">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider bg-accent/20 px-3 py-1 rounded-full mb-4"><ShieldCheck className="w-3.5 h-3.5"/>Restricted access</div>
          <h2 className="font-display text-4xl font-semibold mb-3">Choose a new password.</h2>
          <p className="text-primary-foreground/70 max-w-md">This link is single-use and expires 30 minutes after it was sent.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-5">
          <h1 className="font-display text-3xl font-semibold">Set new password</h1>
          {!token ? (
            <div className="rounded-md border bg-muted/40 p-4 text-sm" data-testid="internal-reset-no-token">
              <div className="font-medium mb-1">This link is invalid.</div>
              <div className="text-muted-foreground">Request a new reset link from <Link to="/internal-admin/forgot-password" className="text-accent hover:underline">internal forgot password</Link>.</div>
            </div>
          ) : done ? (
            <div className="rounded-md border bg-muted/40 p-4 text-sm" data-testid="internal-reset-success">
              <div className="font-medium mb-1">Password updated.</div>
              <div className="text-muted-foreground">Redirecting you to internal login…</div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="internal-reset-password-form">
              <div>
                <Label>New password</Label>
                <Input type="password" required minLength={8} value={pwd} onChange={(e) => setPwd(e.target.value)} className="mt-1.5" data-testid="internal-reset-password-input"/>
                <div className="text-xs text-muted-foreground mt-1">At least 8 characters.</div>
              </div>
              <div>
                <Label>Confirm new password</Label>
                <Input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" data-testid="internal-reset-password-confirm"/>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90" data-testid="internal-reset-submit">
                {loading ? "Updating…" : "Update password"}
              </Button>
            </form>
          )}
          <div className="text-sm"><Link to="/internal-admin/login" className="text-accent hover:underline">← Back to internal login</Link></div>
        </div>
      </div>
    </div>
  );
}


// ---------- Layout (sidebar shell for internal admin pages) ----------
const navItems = [
  { to: "/internal-admin", icon: LayoutDashboard, label: "Overview", end: true },
  { to: "/internal-admin/operations", icon: ShieldCheck, label: "AI Operations" },
  { to: "/internal-admin/diagnostics", icon: AlertCircle, label: "Diagnostics" },
  { to: "/internal-admin/tenants", icon: Building2, label: "Tenants / Customers" },
  { to: "/internal-admin/leads", icon: Users, label: "Global Leads" },
  { to: "/internal-admin/escalations", icon: Flame, label: "Escalations" },
  { to: "/internal-admin/sessions", icon: Brain, label: "Session Intelligence" },
  { to: "/internal-admin/brain-trace", icon: Activity, label: "Brain Trace" },
  { to: "/internal-admin/demo-jobs", icon: Rocket, label: "Demo Jobs" },
  { to: "/internal-admin/email-logs", icon: Mail, label: "Email Logs" },
  // PILOT-1B Sub-sprint B (Feb 2026) — Super Admin OTP Diagnostics.
  { to: "/internal-admin/otp-diagnostics", icon: ShieldCheck, label: "OTP Diagnostics" },
  { to: "/internal-admin/test-checklist", icon: ListChecks, label: "Test Checklist" },
  { to: "/internal-admin/security", icon: KeyRound, label: "Account & Security" },
  // Super-admin controlled Uber Direct TEST dispatch console.
  { to: "/internal-admin/uber-test-dispatch", icon: Truck, label: "Uber TEST Dispatch" },
  // Super-admin PRODUCTION onboarding: stage credentials, preflight,
  // activate, emergency stop. Deliberately separate from the TEST
  // console above and absent from every tenant-facing surface.
  { to: "/internal-admin/uber-production", icon: ShieldAlert, label: "Uber Direct Production" },
  // Sep 2026 — Rudras-owned product images + manual association. Super
  // admin only (enforced server-side by `get_super`).
  { to: "/internal-admin/master-product-images", icon: ImageIcon, label: "Master Product Images" },
];

export function InternalAdminLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-64 border-r bg-card flex flex-col" data-testid="internal-sidebar">
        <div className="p-5 border-b">
          <Link to="/internal-admin" className="flex items-center gap-2">
            <RudrasLogo size="sm" />
            <div>
              <div className="font-display font-semibold text-sm leading-tight">Rudras-AI</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Internal admin</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {navItems.map(it => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition",
                isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
              data-testid={`internal-nav-${it.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
            >
              <it.icon className="w-4 h-4"/>{it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t">
          <div className="text-xs text-muted-foreground mb-2 px-2 truncate">
            <div className="font-medium text-foreground truncate">{user?.full_name}</div>
            <div className="truncate">{user?.email}</div>
            <Badge variant="outline" className="mt-1 text-[10px]">{user?.role}</Badge>
          </div>
          <button onClick={() => { logout(); nav("/"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="internal-logout">
            <LogOut className="w-4 h-4"/>Log out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto p-8 max-w-[1400px]">{children}</main>
    </div>
  );
}

// ---------- Internal Protected route ----------
export function InternalProtected({ children }) {
  const { user, loading, sessionExpired } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  // Stabilization phase: when the session expires mid-page keep the page
  // mounted so the SessionExpiredModal can overlay it instead of an
  // instant redirect that would race the modal.
  if (!user) {
    if (sessionExpired) return children;
    return <Navigate to="/internal-admin/login" replace/>;
  }
  if (!isInternalRole(user.role)) return <Navigate to="/internal-admin/login" replace/>;
  return children;
}

// ---------- Overview ----------
function StatCard({ label, value, sub }) {
  return (
    <Card className="p-5">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="font-display text-3xl font-semibold mt-1">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </Card>
  );
}

export function InternalOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/internal-admin/overview")
      .then(r => setStats(r.data))
      .catch(err => toast.error(err.response?.data?.detail || "Failed to load overview"))
      .finally(() => setLoading(false));
  }, []);
  return (
    <InternalAdminLayout>
      <h1 className="font-display text-4xl font-semibold mb-2">Platform overview</h1>
      <p className="text-muted-foreground mb-8">Aggregate metrics across every tenant on Rudras-AI.</p>
      {loading || !stats ? (
        <div className="text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="internal-overview-grid">
          <StatCard label="Total tenants" value={stats.tenants.total} sub={`${stats.tenants.customer} customer · ${stats.tenants.internal} internal · ${stats.tenants.demo} demo`}/>
          <StatCard label="Leads captured" value={stats.leads}/>
          <StatCard label="Conversations" value={stats.conversations}/>
          <StatCard label="Tokens used" value={stats.tokens?.toLocaleString?.() ?? stats.tokens}/>
          <StatCard label="Emails sent" value={stats.email.sent} sub={stats.email_configured ? "SendGrid live" : "SendGrid not configured"}/>
          <StatCard label="Email failures" value={stats.email.failed}/>
          <StatCard label="Emails not configured" value={stats.email.not_configured}/>
          <StatCard label="Email delivery" value={stats.email_configured ? "Active" : "Off"} sub={stats.email_configured ? "Provider: SendGrid" : "Set SENDGRID_API_KEY"}/>
          <StatCard label="Escalations pending" value={stats.escalations_pending ?? 0} sub="Sales follow-up queue"/>
          <StatCard label="Session summaries" value={stats.session_summaries ?? 0} sub="Auto-generated AI summaries"/>
        </div>
      )}
    </InternalAdminLayout>
  );
}

// ---------- Session Intelligence (Phase B2) ----------
const FOLLOWUP_BADGE = {
  enterprise:  "bg-purple-100 text-purple-800",
  hot:         "bg-rose-100 text-rose-800",
  qualified:   "bg-amber-100 text-amber-800",
  evaluating:  "bg-blue-100 text-blue-800",
  nurture:     "bg-slate-100 text-slate-800",
  exploratory: "bg-zinc-100 text-zinc-700",
};
const URGENCY_BADGE = {
  high:   "bg-rose-100 text-rose-800",
  medium: "bg-amber-100 text-amber-800",
  low:    "bg-slate-100 text-slate-700",
};

export function InternalSessions() {
  const [tab, setTab] = useState("summaries");
  const [summaries, setSummaries] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        api.get("/internal-admin/session-summaries", { params: { limit: 100 } }),
        api.get("/internal-admin/escalations", { params: { limit: 100, status: "pending" } }),
      ]);
      setSummaries(s.data || []);
      setEscalations(e.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load session intelligence");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const updateEscalation = async (id, status) => {
    try {
      await api.put(`/internal-admin/escalations/${id}`, { status });
      toast.success(`Marked ${status}`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  return (
    <InternalAdminLayout>
      <div className="flex items-start justify-between mb-2">
        <h1 className="font-display text-4xl font-semibold" data-testid="internal-sessions-title">Session Intelligence</h1>
        <Button size="sm" variant="outline" onClick={fetchAll} disabled={loading} data-testid="internal-sessions-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}<span className="ml-2">Refresh</span>
        </Button>
      </div>
      <p className="text-muted-foreground mb-6">AI-generated summaries, follow-up classifications, and escalations — read-only insight into every conversation across tenants.</p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="summaries" data-testid="internal-sessions-tab-summaries">Summaries ({summaries.length})</TabsTrigger>
          <TabsTrigger value="escalations" data-testid="internal-sessions-tab-escalations">
            <Flame className="w-3.5 h-3.5 mr-1.5"/>Escalations ({escalations.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="summaries" className="mt-4">
          <Card className="p-0 overflow-hidden" data-testid="internal-sessions-summaries-card">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Tenant</TableHead><TableHead>Session</TableHead><TableHead>Trigger</TableHead>
                <TableHead>Classification</TableHead><TableHead>Follow-up</TableHead>
                <TableHead>Summary</TableHead><TableHead>Next step</TableHead><TableHead>Updated</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {summaries.length === 0 && !loading && (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No session summaries yet. They generate automatically as visitors finish conversations.</TableCell></TableRow>
                )}
                {summaries.map(r => {
                  const s = r.summary || {};
                  const f = r.followup || {};
                  return (
                    <TableRow key={r.id} data-testid={`internal-sessions-summary-row-${r.id}`}>
                      <TableCell><div className="font-medium">{r.company_name || "—"}</div></TableCell>
                      <TableCell className="font-mono text-[11px] text-muted-foreground">{(r.session_id || "").slice(0, 8)}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{r.trigger}</Badge></TableCell>
                      <TableCell><Badge className={cn("text-[10px]", FOLLOWUP_BADGE[s.classification] || "")}>{s.classification || "—"}</Badge></TableCell>
                      <TableCell>
                        <div className="text-xs">
                          <Badge className={cn("text-[10px] mr-1", FOLLOWUP_BADGE[f.classification] || "")}>{f.classification || "—"}</Badge>
                          <Badge className={cn("text-[10px]", URGENCY_BADGE[f.urgency] || "")}>{f.urgency}</Badge>
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">{f.recommended_follow_up}</div>
                      </TableCell>
                      <TableCell className="max-w-md text-xs">{s.text}</TableCell>
                      <TableCell className="text-xs">{s.recommended_next_step}</TableCell>
                      <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">{r.updated_at?.slice(0, 16).replace("T", " ")}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="escalations" className="mt-4">
          <Card className="p-0 overflow-hidden" data-testid="internal-sessions-escalations-card">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Tenant</TableHead><TableHead>Trigger</TableHead><TableHead>Urgency</TableHead>
                <TableHead>Classification</TableHead><TableHead>Recommended response</TableHead>
                <TableHead>Pain</TableHead><TableHead>Created</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {escalations.length === 0 && !loading && (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No pending escalations. Great — the queue is clear.</TableCell></TableRow>
                )}
                {escalations.map(r => (
                  <TableRow key={r.id} data-testid={`internal-sessions-escalation-row-${r.id}`}>
                    <TableCell><div className="font-medium">{r.company_name || "—"}</div></TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{r.trigger}</Badge></TableCell>
                    <TableCell><Badge className={cn("text-[10px]", URGENCY_BADGE[r.urgency] || "")}>{r.urgency}</Badge></TableCell>
                    <TableCell><Badge className={cn("text-[10px]", FOLLOWUP_BADGE[r.classification] || "")}>{r.classification}</Badge></TableCell>
                    <TableCell className="max-w-md text-xs">{r.recommended_response}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground">{(r.pain_points || []).join(", ") || "—"}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">{r.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
                    <TableCell className="space-x-1">
                      <Button size="sm" variant="outline" onClick={() => updateEscalation(r.id, "acknowledged")} data-testid={`internal-escalation-ack-${r.id}`}>Ack</Button>
                      <Button size="sm" variant="outline" onClick={() => updateEscalation(r.id, "resolved")} data-testid={`internal-escalation-resolve-${r.id}`}>Resolved</Button>
                      <Button size="sm" variant="ghost" onClick={() => updateEscalation(r.id, "dismissed")} data-testid={`internal-escalation-dismiss-${r.id}`}>Dismiss</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </InternalAdminLayout>
  );
}

// ---------- Brain Trace (v2.2) ----------
const FLAG_OPTIONS = [
  { id: "skeptical",       label: "Skeptical",      color: "bg-rose-100 text-rose-800" },
  { id: "frustration",     label: "Frustration",    color: "bg-rose-100 text-rose-800" },
  { id: "escalated",       label: "Escalated",      color: "bg-orange-100 text-orange-800" },
  { id: "enterprise",      label: "Enterprise",     color: "bg-purple-100 text-purple-800" },
  { id: "hot_lead",        label: "Hot lead",       color: "bg-amber-100 text-amber-800" },
  { id: "cta_suppressed",  label: "CTA suppressed", color: "bg-blue-100 text-blue-800" },
  { id: "stalled",         label: "Stalled",        color: "bg-slate-100 text-slate-700" },
  { id: "demo_cta_fired",  label: "Demo CTA fired", color: "bg-emerald-100 text-emerald-800" },
];
const FLAG_COLOR = Object.fromEntries(FLAG_OPTIONS.map(f => [f.id, f.color]));

function BrainTraceSessionRow({ row, active, onClick }) {
  return (
    <button
      onClick={() => onClick(row.session_id)}
      data-testid={`brain-trace-session-${row.session_id}`}
      className={cn(
        "w-full text-left p-3 rounded-md border transition-colors",
        active ? "bg-accent/10 border-accent" : "bg-card border-border hover:bg-accent/5"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="font-medium text-sm truncate">{row.company_name || "—"}</div>
        <div className="text-[10px] text-muted-foreground whitespace-nowrap">{row.updated_at?.slice(11, 16)}</div>
      </div>
      <div className="text-[11px] text-muted-foreground truncate mt-0.5">{row.preview || "(no messages yet)"}</div>
      <div className="flex items-center gap-1 mt-2 flex-wrap">
        {(row.behavior_flags || []).map(f => (
          <Badge key={f} className={cn("text-[9px] px-1.5 py-0", FLAG_COLOR[f] || "bg-secondary")}>{f}</Badge>
        ))}
        {row.is_demo && <Badge variant="outline" className="text-[9px] px-1.5 py-0">demo</Badge>}
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">turns: {row.turn_count} · notes: {row.qa_note_count}</div>
    </button>
  );
}

function TraceTurnCard({ assistantMsg, userMsg, index }) {
  const t = assistantMsg.trace || {};
  return (
    <div className="border rounded-lg p-4 bg-card" data-testid={`brain-trace-turn-${index}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-muted-foreground">Turn {index}</div>
        <div className="flex gap-1 flex-wrap">
          {t.emotion && t.emotion !== "neutral" && <Badge className="text-[9px]">{t.emotion}</Badge>}
          {t.state && <Badge variant="outline" className="text-[9px]">{t.state}</Badge>}
          {t.classification && <Badge className="text-[9px]">{t.classification}</Badge>}
          {t.restraint_applied && <Badge className="bg-blue-100 text-blue-800 text-[9px]">restraint</Badge>}
          {t.stalling && <Badge className="bg-slate-100 text-slate-700 text-[9px]">stalled</Badge>}
          {t.escalating && <Badge className="bg-orange-100 text-orange-800 text-[9px]">escalating</Badge>}
        </div>
      </div>

      {userMsg && (
        <div className="text-sm bg-secondary/40 rounded p-2 mb-2">
          <span className="text-[10px] text-muted-foreground font-medium">VISITOR</span>
          <div className="mt-0.5 whitespace-pre-wrap">{userMsg.content}</div>
        </div>
      )}
      <div className="text-sm bg-accent/5 rounded p-2 mb-3">
        <span className="text-[10px] text-muted-foreground font-medium">ASSISTANT</span>
        <div className="mt-0.5 whitespace-pre-wrap">{assistantMsg.content}</div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
        <div><span className="text-muted-foreground">presence:</span> <span className="font-medium">{t.presence_level || "—"}</span> <span className="text-muted-foreground">({t.presence_score ?? "?"})</span></div>
        <div><span className="text-muted-foreground">cadence:</span> <span className="font-medium">{t.cadence_mode || "—"}</span></div>
        <div><span className="text-muted-foreground">budget:</span> <span className="font-medium">{t.sentence_budget ?? "—"}</span> sentences</div>
        <div><span className="text-muted-foreground">framework:</span> <span className="font-medium">{t.framework || "—"}</span></div>
        <div><span className="text-muted-foreground">objection:</span> <span className="font-medium">{t.objection || "—"}</span></div>
        <div><span className="text-muted-foreground">memory:</span> <span className="font-medium">{t.memory_priority_score ?? 0}</span>{t.memory_should_use ? " ✓" : ""}</div>
        <div><span className="text-muted-foreground">return band:</span> <span className="font-medium">{t.return_band || "—"}</span></div>
        <div><span className="text-muted-foreground">score:</span> <span className="font-medium">{t.score ?? "—"}</span></div>
      </div>

      {/* v2.3 — Conversational maturity scores. */}
      {(t.consistency_score != null || t.ai_tone_risk_score != null) && (
        <div className="mt-3 pt-3 border-t border-dashed">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Conversational Maturity</div>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-[11px]">
            <MaturityScore label="consistency" value={t.consistency_score}/>
            <MaturityScore label="ai-tone risk" value={t.ai_tone_risk_score} inverse/>
            <MaturityScore label="repetition" value={t.repetition_score} inverse/>
            <MaturityScore label="smoothness" value={t.emotional_smoothness_score}/>
            <MaturityScore label="long-session" value={t.long_session_quality_score}/>
            <MaturityScore label="listening" value={t.listening_quality_score}/>
          </div>
        </div>
      )}

      {/* Premium Voice Optimization v1 — voice telemetry sub-panel.
          Only renders when this turn carried voice telemetry. */}
      {t.voice && (
        <div className="mt-3 pt-3 border-t border-dashed" data-testid="voice-trace-panel">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Voice Telemetry</div>
            <div className="text-[10px] text-muted-foreground">avg delay {(t.voice.average_response_delay_ms ?? 0)} ms</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-[11px]">
            <MaturityScore label="pacing"       value={t.voice.pacing_quality_score}/>
            <MaturityScore label="consistency"  value={t.voice.voice_consistency_score}/>
            <MaturityScore label="naturalness"  value={t.voice.speech_naturalness_score}/>
            <MaturityScore label="interruptions" value={t.voice.interruption_count} inverse/>
            <MaturityScore label="false-trigs"   value={t.voice.false_trigger_count} inverse/>
            <MaturityScore label="VQI"          value={computeTurnVQI(t.voice)}/>
          </div>
        </div>
      )}
    </div>
  );
}


// Mirror of `voice_quality_index.turn_vqi` on the frontend so the panel can render
// the composite without an extra round-trip.
function computeTurnVQI(v) {
  if (!v) return 0;
  const pacing = v.pacing_quality_score ?? 70;
  const consistency = v.voice_consistency_score ?? 70;
  const naturalness = v.speech_naturalness_score ?? 70;
  const interruptionPenalty = (v.interruption_count || 0) * 6 + (v.false_trigger_count || 0) * 4;
  const interruption = Math.max(0, 100 - interruptionPenalty);
  const avgMs = v.average_response_delay_ms ?? 600;
  let latency;
  if (avgMs <= 600) latency = 100;
  else if (avgMs >= 6000) latency = 0;
  else latency = Math.max(0, 100 - (avgMs - 600) / 54.0);
  const score = pacing * 0.3 + consistency * 0.2 + naturalness * 0.25 + interruption * 0.15 + latency * 0.1;
  return Math.max(0, Math.min(100, Math.round(score)));
}

function MaturityScore({ label, value, inverse = false }) {
  if (value == null) return <div><span className="text-muted-foreground">{label}:</span> —</div>;
  // For inverse scores (risk / repetition), high = bad.
  let cls = "text-emerald-700";
  if (inverse) {
    if (value >= 50) cls = "text-rose-700";
    else if (value >= 25) cls = "text-amber-700";
  } else {
    if (value < 40) cls = "text-rose-700";
    else if (value < 70) cls = "text-amber-700";
  }
  return (
    <div>
      <span className="text-muted-foreground">{label}:</span>{" "}
      <span className={cn("font-semibold", cls)}>{value}</span>
    </div>
  );
}

export function InternalBrainTrace() {
  const [flags, setFlags] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [noteText, setNoteText] = useState("");

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (flags.length) params.flag = flags.join(",");
      const { data } = await api.get("/internal-admin/brain-trace/sessions", { params });
      setSessions(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load sessions");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchSessions(); /* eslint-disable-line */ }, [flags.join(",")]);

  const openSession = async (sid) => {
    setActiveId(sid);
    setLoadingDetail(true);
    setDetail(null);
    try {
      const { data } = await api.get(`/internal-admin/brain-trace/sessions/${sid}`);
      setDetail(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load session detail");
    } finally { setLoadingDetail(false); }
  };

  const toggleFlag = (id) => setFlags(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);

  const addNote = async () => {
    if (!noteText.trim() || !activeId) return;
    try {
      const { data } = await api.post(`/internal-admin/brain-trace/sessions/${activeId}/notes`, { text: noteText.trim() });
      setDetail(d => ({ ...d, qa_notes: [...(d?.qa_notes || []), data] }));
      setNoteText("");
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add note");
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/internal-admin/brain-trace/sessions/${activeId}/notes/${noteId}`);
      setDetail(d => ({ ...d, qa_notes: (d?.qa_notes || []).filter(n => n.id !== noteId) }));
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete note");
    }
  };

  // Pair messages into turns: each assistant message + the user message right before it.
  const turns = (detail?.messages || []).reduce((acc, m, i, arr) => {
    if (m.role === "assistant") {
      const prevUser = i > 0 && arr[i - 1].role === "user" ? arr[i - 1] : null;
      acc.push({ user: prevUser, assistant: m, index: acc.length + 1 });
    }
    return acc;
  }, []);

  return (
    <InternalAdminLayout>
      <div className="flex items-start justify-between mb-2">
        <h1 className="font-display text-4xl font-semibold" data-testid="brain-trace-title">Brain Trace</h1>
        <Button size="sm" variant="outline" onClick={fetchSessions} disabled={loading} data-testid="brain-trace-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}<span className="ml-2">Refresh</span>
        </Button>
      </div>
      <p className="text-muted-foreground mb-4">Replay any conversation turn-by-turn. See exactly why the AI shaped its reply the way it did — what the orchestrator decided, what got suppressed, what tone landed. Filter by behavioral signal to accelerate QA cycles. <strong>Internal only.</strong></p>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="brain-trace-filters">
        {FLAG_OPTIONS.map(f => (
          <button
            key={f.id}
            onClick={() => toggleFlag(f.id)}
            data-testid={`brain-trace-filter-${f.id}`}
            className={cn(
              "text-xs px-2.5 py-1 rounded-full border transition-colors",
              flags.includes(f.id)
                ? `${f.color} border-transparent font-medium`
                : "bg-card text-muted-foreground border-border hover:bg-accent/5"
            )}
          >{f.label}</button>
        ))}
        {flags.length > 0 && (
          <button onClick={() => setFlags([])} className="text-xs text-muted-foreground underline ml-1" data-testid="brain-trace-clear-filters">clear</button>
        )}
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Sessions list */}
        <div className="col-span-12 lg:col-span-4">
          <Card className="p-3">
            <div className="text-xs text-muted-foreground mb-2 px-1">{sessions.length} session{sessions.length === 1 ? "" : "s"}</div>
            <div className="space-y-2 max-h-[68vh] overflow-y-auto pr-1">
              {sessions.length === 0 && !loading && (
                <div className="text-center text-muted-foreground text-sm py-8">No sessions match these filters.</div>
              )}
              {sessions.map(row => (
                <BrainTraceSessionRow key={row.id} row={row} active={row.session_id === activeId} onClick={openSession}/>
              ))}
            </div>
          </Card>
        </div>

        {/* Detail panel */}
        <div className="col-span-12 lg:col-span-8" data-testid="brain-trace-detail">
          {!activeId && (
            <Card className="p-8 text-center text-muted-foreground">
              <Brain className="w-10 h-10 mx-auto mb-3 opacity-40"/>
              <p>Select a session on the left to inspect the orchestration trace.</p>
            </Card>
          )}
          {activeId && loadingDetail && (
            <Card className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto"/></Card>
          )}
          {activeId && detail && !loadingDetail && (
            <>
              {/* Session meta */}
              <Card className="p-4 mb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="font-semibold">{detail.company?.name || "—"}</h2>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Session <span className="font-mono">{detail.session_id?.slice(0, 8)}</span> · {detail.messages?.length || 0} messages · {turns.length} turn(s)
                    </div>
                    <div className="flex gap-1 flex-wrap mt-2">
                      {(detail.behavior_flags || []).map(f => (
                        <Badge key={f} className={cn("text-[9px]", FLAG_COLOR[f] || "bg-secondary")}>{f}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>

              {/* QA notes */}
              <Card className="p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <StickyNote className="w-4 h-4"/>
                  <h3 className="font-medium text-sm">QA notes ({detail.qa_notes?.length || 0})</h3>
                </div>
                <div className="space-y-2 mb-3">
                  {(detail.qa_notes || []).map(n => (
                    <div key={n.id} className="text-xs bg-secondary/30 rounded p-2 flex items-start justify-between gap-2" data-testid={`brain-trace-note-${n.id}`}>
                      <div className="flex-1">
                        <div>{n.text}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">{n.author} · {n.created_at?.slice(0, 16).replace("T", " ")}{n.turn_index ? ` · turn ${n.turn_index}` : ""}</div>
                      </div>
                      <button onClick={() => deleteNote(n.id)} className="text-muted-foreground hover:text-rose-700" data-testid={`brain-trace-note-delete-${n.id}`}><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a QA note — e.g. 'Excellent objection handling' or 'Too verbose'"
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") addNote(); }}
                    maxLength={1000}
                    data-testid="brain-trace-note-input"
                  />
                  <Button size="sm" onClick={addNote} disabled={!noteText.trim()} data-testid="brain-trace-note-submit">Add</Button>
                </div>
              </Card>

              {/* Turn-by-turn replay */}
              <div className="space-y-3">
                {turns.length === 0 && <Card className="p-6 text-center text-muted-foreground text-sm">No assistant turns yet.</Card>}
                {turns.map(turn => (
                  <TraceTurnCard key={turn.index} userMsg={turn.user} assistantMsg={turn.assistant} index={turn.index}/>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </InternalAdminLayout>
  );
}

// ---------- Demo Jobs (P1) ----------
const JOB_STATUS_BADGE = {
  queued:     "bg-slate-100 text-slate-800",
  crawling:   "bg-blue-100 text-blue-800",
  extracting: "bg-blue-100 text-blue-800",
  embedding:  "bg-indigo-100 text-indigo-800",
  generating: "bg-amber-100 text-amber-800",
  ready:      "bg-emerald-100 text-emerald-800",
  failed:     "bg-rose-100 text-rose-800",
  expired:    "bg-zinc-100 text-zinc-700",
};

export function InternalDemoJobs() {
  const [tab, setTab] = useState("active");
  const [jobs, setJobs] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [j, h] = await Promise.all([
        api.get("/internal-admin/demo-jobs", { params: { limit: 100 } }),
        api.get("/internal-admin/demo-cleanup-history", { params: { limit: 20 } }),
      ]);
      setJobs(j.data || []);
      setHistory(h.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load demo jobs");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const triggerCleanup = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/cleanup/expired-demos");
      toast.success(`Swept ${data.demos_deleted} expired demo(s)`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cleanup failed (super_admin required)");
    } finally { setCleaning(false); }
  };

  const activeStatuses = new Set(["queued", "crawling", "extracting", "embedding", "generating"]);
  const active = jobs.filter(j => activeStatuses.has(j.status));
  const ready = jobs.filter(j => j.status === "ready");
  const failed = jobs.filter(j => j.status === "failed");

  const renderRow = (j) => (
    <TableRow key={j.id} data-testid={`internal-demo-job-row-${j.id}`}>
      <TableCell><div className="font-medium">{j.business_name}</div><div className="text-[11px] text-muted-foreground">{j.website_host}</div></TableCell>
      <TableCell><Badge className={cn("text-[10px]", JOB_STATUS_BADGE[j.status] || "")}>{j.status}</Badge></TableCell>
      <TableCell>
        <div className="w-32">
          <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-accent transition-all" style={{ width: `${j.progress || 0}%` }}/>
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">{j.progress || 0}% — {j.stage_label || j.status}</div>
        </div>
      </TableCell>
      <TableCell className="font-mono text-[11px]">{j.slug || "—"}</TableCell>
      <TableCell>{j.reused ? <Badge variant="outline" className="text-[10px]">reused</Badge> : <span className="text-[11px] text-muted-foreground">—</span>}</TableCell>
      <TableCell className="text-xs">
        {j.stats?.chunks ? `${j.stats.chunks} chunks` : "—"}
        {j.stats?.duration_ms ? <div className="text-[10px] text-muted-foreground">{(j.stats.duration_ms / 1000).toFixed(1)}s</div> : null}
      </TableCell>
      <TableCell className="max-w-sm text-xs text-rose-700">{j.error || ""}</TableCell>
      <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">{j.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
    </TableRow>
  );

  return (
    <InternalAdminLayout>
      <div className="flex items-start justify-between mb-2">
        <h1 className="font-display text-4xl font-semibold" data-testid="internal-demo-jobs-title">Demo Jobs</h1>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={fetchAll} disabled={loading} data-testid="internal-demo-jobs-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}<span className="ml-2">Refresh</span>
          </Button>
          <Button size="sm" onClick={triggerCleanup} disabled={cleaning} data-testid="internal-demo-jobs-cleanup">
            {cleaning ? <Loader2 className="w-4 h-4 animate-spin"/> : <Rocket className="w-4 h-4"/>}<span className="ml-2">Run cleanup</span>
          </Button>
        </div>
      </div>
      <p className="text-muted-foreground mb-6">Background website-crawl jobs + automatic expired-demo sweeps. The cron runs every 15 minutes; you can also trigger it manually here.</p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="active" data-testid="demo-jobs-tab-active">Active ({active.length})</TabsTrigger>
          <TabsTrigger value="ready" data-testid="demo-jobs-tab-ready">Ready ({ready.length})</TabsTrigger>
          <TabsTrigger value="failed" data-testid="demo-jobs-tab-failed">Failed ({failed.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="demo-jobs-tab-history">Cleanup history ({history.length})</TabsTrigger>
        </TabsList>

        {["active", "ready", "failed"].map(name => {
          const rows = name === "active" ? active : name === "ready" ? ready : failed;
          return (
            <TabsContent key={name} value={name} className="mt-4">
              <Card className="p-0 overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Business</TableHead><TableHead>Status</TableHead><TableHead>Progress</TableHead>
                    <TableHead>Slug</TableHead><TableHead>Reused</TableHead><TableHead>Stats</TableHead>
                    <TableHead>Error</TableHead><TableHead>Created</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {rows.length === 0 && !loading && (
                      <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No {name} jobs right now.</TableCell></TableRow>
                    )}
                    {rows.map(renderRow)}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>
          );
        })}

        <TabsContent value="history" className="mt-4">
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Run at</TableHead><TableHead>Demos</TableHead><TableHead>Chunks</TableHead>
                <TableHead>Docs</TableHead><TableHead>Conversations</TableHead><TableHead>Leads</TableHead>
                <TableHead>Jobs marked expired</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {history.length === 0 && !loading && (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No cleanup runs yet.</TableCell></TableRow>
                )}
                {history.map((h) => (
                  <TableRow key={h.id} data-testid={`internal-demo-cleanup-row-${h.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{h.ran_at?.slice(0, 19).replace("T", " ")}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{h.demos_deleted}</Badge></TableCell>
                    <TableCell>{h.chunks_deleted}</TableCell>
                    <TableCell>{h.docs_deleted}</TableCell>
                    <TableCell>{h.conversations_deleted}</TableCell>
                    <TableCell>{h.leads_deleted}</TableCell>
                    <TableCell>{h.jobs_marked_expired}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </InternalAdminLayout>
  );
}

// ---------- Tenants ----------
const TENANT_STATUS_OPTIONS = [
  { value: "all",      label: "All statuses" },
  { value: "active",   label: "Active" },
  { value: "disabled", label: "Disabled" },
  { value: "trial",    label: "Trial" },
  { value: "expired",  label: "Expired" },
];

// Two-step Cleanup modal: dry-run → typed-confirm → execute.
// Super-admin only — the button is hidden for everyone else and the
// backend enforces the same gate (POST /internal-admin/tenants/cleanup/*).
function TenantCleanupModal({ open, onClose, onCompleted }) {
  const [phase, setPhase] = useState("idle"); // idle | dryrun | review | executing | done
  const [report, setReport] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [executeResult, setExecuteResult] = useState(null);
  const [error, setError] = useState("");

  const reset = () => {
    setPhase("idle"); setReport(null); setConfirmText("");
    setExecuteResult(null); setError("");
  };
  const handleClose = () => { reset(); onClose?.(); };

  const runDryRun = async () => {
    setError(""); setPhase("dryrun");
    try {
      const { data } = await api.post("/internal-admin/tenants/cleanup/dry-run");
      setReport(data);
      setPhase("review");
    } catch (err) {
      setError(err.response?.data?.detail || "Dry-run failed");
      setPhase("idle");
    }
  };

  const expectedConfirm = report?.confirm_token_required || "EXECUTE-YYYY-MM-DD";
  const canExecute = phase === "review"
    && (report?.candidates?.length || 0) > 0
    && confirmText.trim() === expectedConfirm;

  const runExecute = async () => {
    if (!canExecute) return;
    setError(""); setPhase("executing");
    try {
      const { data } = await api.post(
        "/internal-admin/tenants/cleanup/execute",
        { confirm: confirmText.trim() },
      );
      setExecuteResult(data);
      setPhase("done");
      toast.success(`Purged ${data.purged_count} tenant(s).`);
      onCompleted?.();
    } catch (err) {
      setError(err.response?.data?.detail || "Execute failed");
      setPhase("review");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        className="max-w-4xl max-h-[85vh] overflow-y-auto"
        data-testid="tenant-cleanup-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-destructive" />
            Cleanup Disabled Tenants
          </DialogTitle>
          <DialogDescription>
            Permanently delete every disabled tenant and all tenant-scoped
            records. Always run the dry-run first.
          </DialogDescription>
        </DialogHeader>

        {phase === "idle" && (
          <div className="space-y-4">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm">
              <div className="font-semibold text-amber-900 mb-1">Step 1 — Dry-run</div>
              <div className="text-amber-800">
                Scans every tenant where <code>is_enabled = false</code> and
                returns the candidate list + per-collection record counts.
                <strong> No data is touched.</strong>
              </div>
            </div>
            <Button
              onClick={runDryRun}
              data-testid="cleanup-run-dryrun-btn"
              className="w-full"
            >
              Run Dry-Run
            </Button>
          </div>
        )}

        {phase === "dryrun" && (
          <div className="py-12 flex flex-col items-center gap-3 text-sm text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin" />
            Scanning disabled tenants…
          </div>
        )}

        {(phase === "review" || phase === "executing") && report && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <Card className="p-3" data-testid="cleanup-stat-disabled">
                <div className="text-xs text-muted-foreground">Disabled scanned</div>
                <div className="text-2xl font-semibold">{report.total_disabled}</div>
              </Card>
              <Card className="p-3" data-testid="cleanup-stat-candidates">
                <div className="text-xs text-muted-foreground">Will be deleted</div>
                <div className="text-2xl font-semibold text-destructive">
                  {report.candidates?.length || 0}
                </div>
              </Card>
              <Card className="p-3" data-testid="cleanup-stat-skipped">
                <div className="text-xs text-muted-foreground">Protected / skipped</div>
                <div className="text-2xl font-semibold">{report.skipped?.length || 0}</div>
              </Card>
            </div>

            <div>
              <div className="text-sm font-semibold mb-2">Deletion Candidates</div>
              {(report.candidates?.length || 0) === 0 ? (
                <div className="text-sm text-muted-foreground italic px-2 py-3">
                  No eligible disabled tenants found.
                </div>
              ) : (
                <Card className="p-0 overflow-hidden" data-testid="cleanup-candidates-card">
                  <div className="max-h-72 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Company</TableHead>
                          <TableHead>Customer ID</TableHead>
                          <TableHead>Slug</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead className="text-right">Records</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.candidates.map((c) => {
                          const total = Object.values(c.record_counts_by_collection || {}).reduce((a, b) => a + b, 0);
                          return (
                            <TableRow key={c.company_id} data-testid={`cleanup-candidate-row-${c.company_id}`}>
                              <TableCell>
                                <div className="font-medium">{c.company_name}</div>
                                <div className="text-[10px] text-muted-foreground">{c.tenant_type}</div>
                              </TableCell>
                              <TableCell className="font-mono text-xs">{c.customer_reference_id || "—"}</TableCell>
                              <TableCell className="font-mono text-xs">{c.slug || "—"}</TableCell>
                              <TableCell><Badge variant="destructive">disabled</Badge></TableCell>
                              <TableCell className="text-xs">{(c.created_at || "").slice(0, 10) || "—"}</TableCell>
                              <TableCell className="text-right font-mono text-xs" title={JSON.stringify(c.record_counts_by_collection)}>
                                {total}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              )}
            </div>

            {report.projected_deletion_counts && Object.keys(report.projected_deletion_counts).length > 0 && (
              <div>
                <div className="text-sm font-semibold mb-2">Projected deletions by collection</div>
                <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="cleanup-projected-counts">
                  {Object.entries(report.projected_deletion_counts).sort().map(([k, v]) => (
                    <span key={k} className="font-mono px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200">
                      {k}: {v}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(report.skipped?.length || 0) > 0 && (
              <details className="text-xs" data-testid="cleanup-skipped-details">
                <summary className="cursor-pointer text-muted-foreground">
                  {report.skipped.length} protected/skipped tenant(s) — click to expand
                </summary>
                <ul className="mt-2 space-y-1 pl-4 list-disc">
                  {report.skipped.map((s) => (
                    <li key={s.company_id}>
                      <span className="font-medium">{s.company_name}</span>
                      <span className="text-muted-foreground"> · {s.slug || "—"} · {s.skip_reason}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {(report.candidates?.length || 0) > 0 && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 space-y-2">
                <div className="text-sm font-semibold text-destructive flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Step 2 — Permanent Deletion
                </div>
                <div className="text-xs text-destructive/90">
                  This will permanently delete all rows above. Type the
                  confirmation token exactly to enable the button:
                </div>
                <code className="block font-mono text-xs px-2 py-1 rounded bg-background border" data-testid="cleanup-expected-token">
                  {expectedConfirm}
                </code>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={expectedConfirm}
                  data-testid="cleanup-confirm-input"
                  disabled={phase === "executing"}
                  className="font-mono"
                />
              </div>
            )}

            {error && (
              <div className="text-sm text-destructive" data-testid="cleanup-error">{error}</div>
            )}
          </div>
        )}

        {phase === "done" && executeResult && (
          <div className="space-y-3">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm">
              <div className="font-semibold text-emerald-900 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Cleanup complete
              </div>
              <div className="text-emerald-800 mt-1">
                Purged <strong>{executeResult.purged_count}</strong> tenant(s).
                Skipped <strong>{executeResult.skipped_count}</strong>.
              </div>
            </div>
            <div>
              <div className="text-sm font-semibold mb-2">Records deleted by collection</div>
              <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="cleanup-deletion-counts">
                {Object.entries(executeResult.deletion_counts_by_collection || {}).sort().map(([k, v]) => (
                  <span key={k} className="font-mono px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200">
                    {k}: {v}
                  </span>
                ))}
              </div>
            </div>
            {(executeResult.purged_tenants?.length || 0) > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">
                  {executeResult.purged_tenants.length} purged tenant(s) — click to expand
                </summary>
                <ul className="mt-2 space-y-1 pl-4 list-disc">
                  {executeResult.purged_tenants.map((t) => (
                    <li key={t.company_id}>
                      <span className="font-medium">{t.company_name}</span>
                      <span className="text-muted-foreground"> · {t.primary_user_email || "—"}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {phase === "review" && (report?.candidates?.length || 0) > 0 && (
            <Button
              variant="destructive"
              onClick={runExecute}
              disabled={!canExecute || phase === "executing"}
              data-testid="cleanup-execute-btn"
            >
              {phase === "executing" ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Executing…</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2"/>Execute Permanent Delete</>
              )}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={phase === "executing"}
            data-testid="cleanup-close-btn"
          >
            {phase === "done" ? "Close" : "Cancel"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InternalTenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [impersonatingId, setImpersonatingId] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const { user, updateToken } = useAuth();
  const nav = useNavigate();
  const canToggle = user?.role === "super_admin" || user?.role === "support";
  const canImpersonate = user?.role === "super_admin";
  const canCleanup = user?.role === "super_admin";

  const fetchTenants = useCallback(async (q = "", status = "all") => {
    setLoading(true);
    try {
      // `?q=` searches across `customer_reference_id`, UUID, slug, name.
      // `?status=` filters by lifecycle: active / disabled / trial / expired.
      const params = {};
      if (q && q.trim()) params.q = q.trim();
      if (status && status !== "all") params.status = status;
      const { data } = await api.get("/internal-admin/tenants", {
        params: Object.keys(params).length ? params : undefined,
      });
      setTenants(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load tenants"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchTenants("", statusFilter); }, [fetchTenants, statusFilter]);

  // Debounce search input + react to status changes. 300ms is a
  // comfortable typing pause.
  useEffect(() => {
    const t = setTimeout(() => { fetchTenants(searchQ, statusFilter); }, 300);
    return () => clearTimeout(t);
  }, [fetchTenants, searchQ, statusFilter]);

  const toggle = async (id) => {
    try {
      await api.put(`/internal-admin/tenants/${id}/toggle`);
      fetchTenants();
    } catch (err) { toast.error(err.response?.data?.detail || "Toggle failed"); }
  };

  // Phase 3G — Retail mode toggle. Super-admin only (same gate as
  // POST /api/admin/companies/{id}/retail-mode). Flips the per-tenant
  // `retail_mode_enabled` flag without touching `is_enabled`.
  const toggleRetail = async (id, current) => {
    try {
      await api.post(`/admin/companies/${id}/retail-mode`, { enabled: !current });
      fetchTenants();
    } catch (err) { toast.error(err.response?.data?.detail || "Retail toggle failed"); }
  };

  // Aug 2026 — Kitchen Monitoring toggle (master-switch containment
  // corrections, F-2 resolution). Super-admin only (same gate as
  // POST /api/admin/companies/{id}/kitchen-monitoring). Flips the
  // per-tenant `kitchen_monitoring_enabled` flag, default OFF. This
  // toggle alone does not start any scanning — the platform-wide
  // OPS_ALERTS_ENABLED master switch must ALSO be on before the KDS
  // watchdog evaluates this tenant's kitchen orders.
  const toggleKitchenMonitoring = async (id, current) => {
    try {
      await api.post(`/admin/companies/${id}/kitchen-monitoring`, { enabled: !current });
      fetchTenants();
    } catch (err) { toast.error(err.response?.data?.detail || "Kitchen monitoring toggle failed"); }
  };

  const impersonate = async (companyId, companyName) => {
    if (!canImpersonate) return;
    setImpersonatingId(companyId);
    try {
      const { data } = await api.post(`/internal-admin/tenants/${companyId}/impersonate`);
      // Back up the super-admin's current token BEFORE installing the
      // impersonation token. Critical for "Exit Impersonation".
      const currentToken = localStorage.getItem("ren_token");
      if (currentToken) localStorage.setItem("sa_token_backup", currentToken);
      localStorage.setItem("impersonation_company", data?.company?.name || companyName);
      localStorage.setItem("ren_token", data.token);
      // Hard reload to /dashboard so AuthProvider re-bootstraps with the new
      // token. Soft-navigation would race InternalProtected and bounce us
      // back to /internal-admin/login the instant React state flips to
      // business_admin (since InternalProtected requires an internal role).
      window.location.replace("/dashboard");
    } catch (err) {
      // Endpoint failed BEFORE we installed the impersonation token — no
      // backup was set, so the super-admin's session is intact. Nothing to
      // restore.
      toast.error(err.response?.data?.detail || "Impersonation failed");
      setImpersonatingId("");
    }
  };

  return (
    <InternalAdminLayout>
      <div className="flex items-start justify-between mb-2">
        <h1 className="font-display text-4xl font-semibold">Tenants / Customers</h1>
        <div className="flex items-center gap-2">
          {canCleanup && (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setCleanupOpen(true)}
              data-testid="open-tenant-cleanup-btn"
              title="Permanently delete all disabled tenants"
            >
              <Trash2 className="w-4 h-4 mr-2"/>Cleanup Disabled
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={fetchTenants} disabled={loading} data-testid="internal-tenants-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}<span className="ml-2">Refresh</span>
          </Button>
        </div>
      </div>
      <p className="text-muted-foreground mb-4">
        Every company on the platform — customer demos, paid customers, and the internal Rudras-AI tenant.
        {canImpersonate && " Click a company name to open that tenant's dashboard as super-admin."}
      </p>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          placeholder="Search by Customer ID (REST0001), UUID, slug, or company name…"
          data-testid="internal-tenants-search"
          className="flex-1 min-w-[260px] max-w-md px-3 py-2 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-accent/60"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="tenant-status-filter"
          className="px-3 py-2 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-accent/60"
          title="Filter tenants by lifecycle status"
        >
          {TENANT_STATUS_OPTIONS.map(opt => (
            <option
              key={opt.value}
              value={opt.value}
              data-testid={`tenant-status-option-${opt.value}`}
            >{opt.label}</option>
          ))}
        </select>
        <span className="text-xs text-muted-foreground" data-testid="tenant-result-count">
          {tenants.length} tenant{tenants.length === 1 ? "" : "s"}
        </span>
      </div>
      <Card className="p-0 overflow-hidden" data-testid="internal-tenants-card">
        <Table>
          <TableHeader><TableRow><TableHead>Company</TableHead><TableHead data-testid="tenant-customer-id-header">Customer ID</TableHead><TableHead>Type</TableHead><TableHead>Sales</TableHead><TableHead>Plan</TableHead><TableHead>Trial</TableHead><TableHead>Status</TableHead><TableHead>Slug</TableHead><TableHead>Branding</TableHead><TableHead>Convos</TableHead><TableHead>Leads</TableHead><TableHead>Resources</TableHead><TableHead>Tokens</TableHead><TableHead>Retail</TableHead><TableHead title="Monitors this tenant's kitchen orders for unusually long processing times. Enable only when this tenant actively uses the Rudras-AI kitchen display workflow.">Kitchen Monitoring</TableHead>{canToggle && <TableHead></TableHead>}</TableRow></TableHeader>
          <TableBody>
            {tenants.map(c => {
              const trialEnd = c.trial_end ? new Date(c.trial_end) : null;
              const daysLeft = trialEnd ? Math.ceil((trialEnd - new Date()) / 86400000) : null;
              const trialLabel = c.plan_status === "trial" && daysLeft !== null
                ? (daysLeft > 0 ? `${daysLeft}d left` : "expired")
                : "—";
              const isImpersonating = impersonatingId === c.id;
              return (
              <TableRow key={c.id} data-testid={`internal-tenant-row-${c.id}`}>
                <TableCell>
                  {canImpersonate ? (
                    <button
                      type="button"
                      onClick={() => impersonate(c.id, c.name)}
                      disabled={isImpersonating || !c.is_enabled}
                      data-testid={`impersonate-tenant-${c.id}`}
                      title={c.is_enabled ? `Open ${c.name}'s dashboard as super-admin` : "Tenant disabled — enable to impersonate"}
                      className="group inline-flex items-center gap-1.5 text-left font-medium hover:text-accent disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-foreground transition-colors"
                    >
                      <span>{c.name}</span>
                      {isImpersonating
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin opacity-70" />
                        : <UserCog className="w-3.5 h-3.5 opacity-0 group-hover:opacity-80 transition-opacity" />}
                    </button>
                  ) : (
                    <div className="font-medium">{c.name}</div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {c.industry || "—"} · {c.contact_email}
                  </div>
                </TableCell>
                <TableCell data-testid={`tenant-customer-id-cell-${c.id}`}>
                  {c.customer_reference_id ? (
                    <button
                      type="button"
                      onClick={() => {
                        try {
                          navigator.clipboard.writeText(c.customer_reference_id);
                          toast.success(`Copied ${c.customer_reference_id}`);
                        } catch (_e) { /* clipboard unavailable */ }
                      }}
                      title="Click to copy"
                      className="font-mono text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200"
                      data-testid={`tenant-customer-id-${c.id}`}
                    >
                      {c.customer_reference_id}
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>{c.is_internal ? <Badge>internal</Badge> : c.is_demo ? <Badge variant="secondary">demo</Badge> : <Badge variant="outline">customer</Badge>}</TableCell>
                <TableCell>{c.sales_model ? <Badge variant="outline" className="uppercase text-[10px]">{c.sales_model}</Badge> : <span className="text-muted-foreground text-xs">—</span>}</TableCell>
                <TableCell><Badge variant="outline">{c.plan_status || c.subscription_plan}</Badge></TableCell>
                <TableCell className="text-xs whitespace-nowrap text-muted-foreground" title={c.trial_end || ""}>{trialLabel}</TableCell>
                <TableCell><Badge variant={c.is_enabled ? "default" : "destructive"}>{c.is_enabled ? "active" : "disabled"}</Badge></TableCell>
                <TableCell className="text-xs font-mono">{c.chatbot_slug}</TableCell>
                <TableCell>{c.has_logo ? <Badge variant="default" className="bg-emerald-100 text-emerald-800">logo</Badge> : <span className="text-muted-foreground text-xs">—</span>}</TableCell>
                <TableCell>{c.conversations}</TableCell>
                <TableCell>{c.leads}</TableCell>
                <TableCell>
                  <div className="text-sm">{c.resources ?? 0}</div>
                  {(c.websites_imported || 0) > 0 && <div className="text-[10px] text-muted-foreground">{c.websites_imported} website</div>}
                </TableCell>
                <TableCell>{c.tokens?.toLocaleString()}</TableCell>
                <TableCell>
                  {canToggle ? (
                    <Button
                      size="sm"
                      variant={c.retail_mode_enabled ? "default" : "outline"}
                      onClick={() => toggleRetail(c.id, !!c.retail_mode_enabled)}
                      data-testid={`internal-tenant-retail-toggle-${c.id}`}
                      title={c.retail_mode_enabled ? "Disable Retail mode" : "Enable Retail mode"}
                    >
                      {c.retail_mode_enabled ? "Retail: ON" : "Retail: OFF"}
                    </Button>
                  ) : (
                    <Badge variant={c.retail_mode_enabled ? "default" : "outline"}>
                      {c.retail_mode_enabled ? "on" : "off"}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {(() => {
                    // Strict-Boolean display (independent corrective review
                    // correction, Aug 2026): only a literal `true` counts as
                    // On, mirroring the backend's own strict `is True` check
                    // in kds_watchdog.py. Missing, null, false, strings,
                    // numbers, arrays, and objects must all display Off —
                    // a truthiness check (`!!value`, or using the raw value
                    // directly in a ternary) would incorrectly show "On" for
                    // e.g. a stray array/object value, which JavaScript
                    // treats as truthy but the backend does not.
                    const kitchenMonitoringOn = c.kitchen_monitoring_enabled === true;
                    return canToggle ? (
                      <Button
                        size="sm"
                        variant={kitchenMonitoringOn ? "default" : "outline"}
                        onClick={() => toggleKitchenMonitoring(c.id, kitchenMonitoringOn)}
                        data-testid={`internal-tenant-kitchen-monitoring-toggle-${c.id}`}
                        title="Monitors this tenant's kitchen orders for unusually long processing times. Enable only when this tenant actively uses the Rudras-AI kitchen display workflow."
                      >
                        {kitchenMonitoringOn ? "Kitchen Monitoring: ON" : "Kitchen Monitoring: OFF"}
                      </Button>
                    ) : (
                      <Badge variant={kitchenMonitoringOn ? "default" : "outline"}>
                        {kitchenMonitoringOn ? "on" : "off"}
                      </Badge>
                    );
                  })()}
                </TableCell>
                {canToggle && <TableCell><Button size="sm" variant="outline" onClick={() => toggle(c.id)} data-testid={`internal-tenant-toggle-${c.id}`}>{c.is_enabled ? "Disable" : "Enable"}</Button></TableCell>}
              </TableRow>
            );})}
          </TableBody>
        </Table>
      </Card>
      {canCleanup && (
        <TenantCleanupModal
          open={cleanupOpen}
          onClose={() => setCleanupOpen(false)}
          onCompleted={() => fetchTenants(searchQ, statusFilter)}
        />
      )}
    </InternalAdminLayout>
  );
}

// ---------- Global Leads ----------
export function InternalLeads() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const fetchLeads = async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get("/internal-admin/leads", { params: { limit: 200 } });
      setData(payload);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load leads"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchLeads(); }, []);

  return (
    <InternalAdminLayout>
      <div className="flex items-start justify-between mb-2">
        <h1 className="font-display text-4xl font-semibold">Global leads</h1>
        <Button size="sm" variant="outline" onClick={fetchLeads} disabled={loading} data-testid="internal-leads-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}<span className="ml-2">Refresh</span>
        </Button>
      </div>
      <p className="text-muted-foreground mb-6">Every lead captured across every tenant. Use the <code>Tenant</code> column to verify routing.</p>
      <Card className="p-0 overflow-hidden" data-testid="internal-leads-card">
        <Table>
          <TableHeader><TableRow><TableHead>Tenant</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Type</TableHead><TableHead>When</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.items.map(l => (
              <TableRow key={l.id} data-testid={`internal-lead-row-${l.id}`}>
                <TableCell>
                  <div className="font-medium">{l.company_name || "—"}</div>
                  {l.is_internal_tenant && <Badge className="text-[10px] mt-0.5">internal</Badge>}
                </TableCell>
                <TableCell>{l.name}</TableCell>
                <TableCell>{l.email}</TableCell>
                <TableCell><Badge variant="outline">{l.inquiry_type}</Badge></TableCell>
                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!loading && data.items.length === 0 && <div className="p-10 text-center text-muted-foreground" data-testid="internal-leads-empty">No leads captured yet.</div>}
      </Card>
    </InternalAdminLayout>
  );
}

// ---------- Global Email Logs ----------
const STATUS_META = {
  sent:           { label: "Sent",           icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  failed:         { label: "Failed",         icon: XCircle,      cls: "text-red-700 bg-red-50 border-red-200" },
  not_configured: { label: "Not configured", icon: AlertCircle,  cls: "text-amber-700 bg-amber-50 border-amber-200" },
};
function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status || "unknown", icon: AlertCircle, cls: "text-muted-foreground bg-muted border" };
  const Icon = m.icon;
  return (
    <span className={cn("inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium", m.cls)}>
      <Icon className="w-3 h-3"/>{m.label}
    </span>
  );
}

export function InternalEmailLogs() {
  const [data, setData] = useState({ items: [], summary: {}, email_configured: false });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchLogs = useCallback(async (status) => {
    setLoading(true);
    try {
      const params = { limit: 200, ...(status && status !== "all" ? { status } : {}) };
      const { data: payload } = await api.get("/internal-admin/email-logs", { params });
      setData(payload);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load email logs"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchLogs("all"); }, [fetchLogs]);
  const setTab = (val) => { setFilter(val); fetchLogs(val); };

  const counts = {
    all: (data.summary.sent || 0) + (data.summary.failed || 0) + (data.summary.not_configured || 0),
    sent: data.summary.sent || 0,
    failed: data.summary.failed || 0,
    not_configured: data.summary.not_configured || 0,
  };

  return (
    <InternalAdminLayout>
      <div className="flex items-start justify-between mb-2">
        <h1 className="font-display text-4xl font-semibold">Email logs (all tenants)</h1>
        <Button size="sm" variant="outline" onClick={() => fetchLogs(filter)} disabled={loading} data-testid="internal-email-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}<span className="ml-2">Refresh</span>
        </Button>
      </div>
      <Card className={cn("p-4 mb-6 border", data.email_configured ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200")} data-testid="internal-email-config-banner">
        <div className="text-sm">
          {data.email_configured
            ? <div><span className="font-medium text-emerald-900">SendGrid is active</span><span className="text-emerald-800/80"> — sending as <code>{data.from_name ? `${data.from_name} <${data.from_email}>` : data.from_email}</code>.</span></div>
            : <div><span className="font-medium text-amber-900">SendGrid not configured</span><span className="text-amber-800/80"> — email attempts are stored for audit but no mail is leaving the system.</span></div>}
        </div>
      </Card>
      <Tabs value={filter} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="internal-email-tab-all">All <span className="ml-1.5 text-xs text-muted-foreground">{counts.all}</span></TabsTrigger>
          <TabsTrigger value="sent" data-testid="internal-email-tab-sent">Sent <span className="ml-1.5 text-xs text-muted-foreground">{counts.sent}</span></TabsTrigger>
          <TabsTrigger value="failed" data-testid="internal-email-tab-failed">Failed <span className="ml-1.5 text-xs text-muted-foreground">{counts.failed}</span></TabsTrigger>
          <TabsTrigger value="not_configured" data-testid="internal-email-tab-notc">Not configured <span className="ml-1.5 text-xs text-muted-foreground">{counts.not_configured}</span></TabsTrigger>
        </TabsList>
        <TabsContent value={filter}>
          <Card className="p-0 overflow-hidden mt-4" data-testid="internal-email-card">
            <Table>
              <TableHeader><TableRow><TableHead>Status</TableHead><TableHead>Tenant</TableHead><TableHead>Recipient</TableHead><TableHead>Subject</TableHead><TableHead>Provider</TableHead><TableHead>When</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.items.map(log => (
                  <TableRow key={log.id} data-testid={`internal-email-row-${log.id}`}>
                    <TableCell><StatusBadge status={log.status}/></TableCell>
                    <TableCell className="text-sm">{log.company_name || "—"}</TableCell>
                    <TableCell className="font-medium">{log.to}</TableCell>
                    <TableCell className="max-w-[260px] truncate" title={log.subject}>{log.subject}</TableCell>
                    <TableCell className="text-xs">
                      {log.status === "sent"
                        ? <span className="font-mono">{log.provider_message_id || log.provider_status || "OK"}</span>
                        : <span className="text-red-700 truncate inline-block max-w-[200px]" title={log.error}>{log.error || "—"}</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{new Date(log.sent_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!loading && data.items.length === 0 && <div className="p-10 text-center text-muted-foreground" data-testid="internal-email-empty">No email logs match this filter.</div>}
          </Card>
        </TabsContent>
      </Tabs>
    </InternalAdminLayout>
  );
}


// PILOT-1B Sub-sprint B (Feb 2026) — Super-admin OTP diagnostics.
// Searchable + filterable list of every OTP and its delivery lifecycle.
// Read-only — no editing. Lives at /internal-admin/otp-diagnostics and
// is restricted to super-admin by InternalProtected + the backend's
// /api/super/otp/diagnostics endpoint.
const OTP_STATUS_STYLES = {
  queued:      "bg-stone-100 text-stone-700 border-stone-200",
  processed:   "bg-sky-100 text-sky-700 border-sky-200",
  delivered:   "bg-emerald-100 text-emerald-700 border-emerald-200",
  opened:      "bg-emerald-100 text-emerald-700 border-emerald-200",
  deferred:    "bg-amber-100 text-amber-800 border-amber-200",
  bounce:      "bg-red-100 text-red-700 border-red-200",
  blocked:     "bg-red-100 text-red-700 border-red-200",
  dropped:     "bg-red-100 text-red-700 border-red-200",
  spam_report: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200",
};

function DeliveryBadge({ status }) {
  const cls = OTP_STATUS_STYLES[status] || "bg-stone-100 text-stone-700 border-stone-200";
  return (
    <Badge variant="outline" className={cls} data-testid={`otp-delivery-badge-${status || "unknown"}`}>
      {status || "—"}
    </Badge>
  );
}

export function InternalOtpDiagnostics() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  const queryRef = useRef(query);
  useEffect(() => { queryRef.current = query; }, [query]);
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      const q = queryRef.current;
      if (q && q.trim()) params.q = q.trim();
      const { data } = await api.get("/super/otp/diagnostics", { params });
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Failed to load OTP diagnostics");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);
  useEffect(() => { fetchList(); }, [fetchList]);

  const openDetail = async (otpId) => {
    try {
      const { data } = await api.get(`/super/otp/diagnostics/${otpId}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiError(err) || "Failed to load OTP detail");
    }
  };

  return (
    <InternalAdminLayout>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="font-display text-4xl font-semibold">OTP Diagnostics</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Lifecycle, delivery status and provider response for every verification code.
            Synced live from the SendGrid Event Webhook.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={fetchList} disabled={loading} data-testid="otp-diag-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      <Card className="p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="otp-diag-filters">
        <Input
          placeholder="Search by recipient email…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && fetchList()}
          className="max-w-sm"
          data-testid="otp-diag-search"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          data-testid="otp-diag-status-filter"
        >
          <option value="all">All delivery statuses</option>
          <option value="queued">queued</option>
          <option value="processed">processed</option>
          <option value="delivered">delivered</option>
          <option value="deferred">deferred</option>
          <option value="bounce">bounce</option>
          <option value="blocked">blocked</option>
          <option value="dropped">dropped</option>
          <option value="spam_report">spam_report</option>
        </select>
        <Button size="sm" onClick={fetchList} disabled={loading} data-testid="otp-diag-apply">
          Apply
        </Button>
      </Card>

      <Card className="p-0 overflow-hidden" data-testid="otp-diag-table-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Issued</TableHead>
              <TableHead>Recipient</TableHead>
              <TableHead>Last 4</TableHead>
              <TableHead>OTP Status</TableHead>
              <TableHead>Delivery</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Attempts</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((row) => (
              <TableRow key={row.id} data-testid={`otp-diag-row-${row.id}`}>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                  {row.issued_at ? new Date(row.issued_at).toLocaleString() : "—"}
                </TableCell>
                <TableCell className="text-sm font-medium">
                  {row.recipient_email || "—"}
                </TableCell>
                <TableCell className="font-mono text-xs">{row.code_last4 || "—"}</TableCell>
                <TableCell>
                  <Badge variant="outline" data-testid={`otp-diag-status-${row.id}`}>{row.status}</Badge>
                </TableCell>
                <TableCell><DeliveryBadge status={row.delivery_status}/></TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[260px] truncate" title={row.delivery_reason || row.latest_event_reason || ""}>
                  {row.delivery_reason || row.latest_event_reason || "—"}
                </TableCell>
                <TableCell className="text-xs">
                  {row.attempts ?? 0} / {row.max_attempts ?? 5}
                </TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost" onClick={() => openDetail(row.id)} data-testid={`otp-diag-detail-${row.id}`}>
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-muted-foreground" data-testid="otp-diag-empty">
            No OTP records match this filter.
          </div>
        )}
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="otp-diag-detail-dialog">
          <DialogHeader>
            <DialogTitle>OTP delivery timeline</DialogTitle>
            <DialogDescription>
              Full lifecycle for this verification code.
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-xs text-muted-foreground">OTP ID</div><div className="font-mono text-xs break-all">{detail.otp?.id}</div></div>
                <div><div className="text-xs text-muted-foreground">Channel</div><div>{detail.otp?.destination_channel}</div></div>
                <div><div className="text-xs text-muted-foreground">Issued</div><div>{detail.otp?.issued_at && new Date(detail.otp.issued_at).toLocaleString()}</div></div>
                <div><div className="text-xs text-muted-foreground">Expires</div><div>{detail.otp?.expires_at && new Date(detail.otp.expires_at).toLocaleString()}</div></div>
                <div><div className="text-xs text-muted-foreground">OTP status</div><div>{detail.otp?.status}</div></div>
                <div><div className="text-xs text-muted-foreground">Delivery status</div><div><DeliveryBadge status={detail.otp?.delivery_status}/></div></div>
                <div><div className="text-xs text-muted-foreground">Verified</div><div>{detail.otp?.verified_at ? new Date(detail.otp.verified_at).toLocaleString() : "—"}</div></div>
                <div><div className="text-xs text-muted-foreground">Attempts</div><div>{detail.otp?.attempts ?? 0} / {detail.otp?.max_attempts ?? 5}</div></div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1.5">Provider events</div>
                <div className="border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader><TableRow><TableHead>Status</TableHead><TableHead>Reason</TableHead><TableHead>Received</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {(detail.events || []).map((ev, idx) => (
                        <TableRow key={ev.sg_event_id || idx} data-testid={`otp-diag-event-${idx}`}>
                          <TableCell><DeliveryBadge status={ev.canonical_status || ev.sg_event_type}/></TableCell>
                          <TableCell className="text-xs">{ev.reason || ev.response || "—"}</TableCell>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{ev.received_at && new Date(ev.received_at).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {(!detail.events || detail.events.length === 0) && (
                    <div className="p-6 text-center text-xs text-muted-foreground" data-testid="otp-diag-events-empty">
                      No SendGrid events received yet for this OTP. (Verify SendGrid → Mail Settings → Event Webhook is enabled.)
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)} data-testid="otp-diag-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </InternalAdminLayout>
  );
}


// ---------- Internal admin: Account & Security ----------
export function InternalSecurity() {
  const { user } = useAuth();
  return (
    <InternalAdminLayout>
      <h1 className="font-display text-4xl font-semibold mb-2">Account &amp; security</h1>
      <p className="text-muted-foreground mb-8">Signed in as <span className="font-medium text-foreground">{user?.email}</span> · role <Badge variant="outline" className="ml-1">{user?.role}</Badge></p>
      <ChangePasswordCard testidPrefix="internal-pwd"/>
    </InternalAdminLayout>
  );
}


// ============== HUMAN ESCALATION QUEUE (Workflow Readiness v1) ==============
const QUEUE_STATUSES = ["all", "new", "acknowledged", "resolved", "dismissed"];
const TEMP_BADGE = {
  hot: "bg-rose-100 text-rose-800",
  warm: "bg-amber-100 text-amber-800",
  cold: "bg-slate-100 text-slate-700",
};
const QSTATUS_BADGE = {
  new: "bg-blue-100 text-blue-800",
  acknowledged: "bg-amber-100 text-amber-800",
  resolved: "bg-emerald-100 text-emerald-800",
  dismissed: "bg-slate-100 text-slate-600",
};
const EMAIL_BADGE = {
  sent: "bg-emerald-100 text-emerald-800",
  not_configured: "bg-slate-100 text-slate-700",
  failed: "bg-rose-100 text-rose-800",
  no_recipient: "bg-amber-100 text-amber-800",
};

export function InternalEscalations() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});

  const fetchRows = async (st = status) => {
    setLoading(true);
    try {
      const r = await api.get("/internal-admin/escalation-queue", { params: { status: st, limit: 100 } });
      setRows(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load escalation queue");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchRows("all"); }, []); // eslint-disable-line

  const updateStatus = async (lead_id, newStatus) => {
    try {
      const note = (noteDraft[lead_id] || "").trim();
      await api.put(`/internal-admin/escalation-queue/${lead_id}`, { status: newStatus, note: note || undefined });
      toast.success(`Marked ${newStatus}`);
      setNoteDraft(prev => ({ ...prev, [lead_id]: "" }));
      fetchRows();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  const addNote = async (lead_id) => {
    const note = (noteDraft[lead_id] || "").trim();
    if (!note) return;
    try {
      await api.post(`/internal-admin/escalation-queue/${lead_id}/notes`, { note });
      toast.success("Note added");
      setNoteDraft(prev => ({ ...prev, [lead_id]: "" }));
      fetchRows();
    } catch (err) { toast.error(err.response?.data?.detail || "Note failed"); }
  };

  return (
    <InternalAdminLayout>
      <div className="flex items-start justify-between mb-2">
        <h1 className="font-display text-4xl font-semibold" data-testid="internal-escalations-title">Escalations queue</h1>
        <Button size="sm" variant="outline" onClick={() => fetchRows()} disabled={loading} data-testid="internal-escalations-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}<span className="ml-2">Refresh</span>
        </Button>
      </div>
      <p className="text-muted-foreground mb-6">AI-flagged hot leads ready for human follow-up. Ack to claim, resolve when handed-off, dismiss false positives. Internal notes are private.</p>

      <div className="flex gap-2 mb-4 flex-wrap">
        {QUEUE_STATUSES.map(s => (
          <button
            key={s}
            onClick={() => { setStatus(s); fetchRows(s); }}
            className={cn(
              "px-3 py-1.5 rounded-md text-xs border transition",
              status === s ? "bg-accent text-accent-foreground border-accent" : "bg-card hover:bg-muted text-muted-foreground"
            )}
            data-testid={`internal-escalations-filter-${s}`}
          >{s}</button>
        ))}
      </div>

      <Card className="p-0 overflow-hidden" data-testid="internal-escalations-card">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Lead</TableHead><TableHead>Tenant</TableHead><TableHead>Temp</TableHead>
            <TableHead>Priority</TableHead><TableHead>Reason</TableHead>
            <TableHead>Pain</TableHead><TableHead>Email</TableHead><TableHead>Status</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && !loading && (
              <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8" data-testid="internal-escalations-empty">No escalation-ready leads match this filter.</TableCell></TableRow>
            )}
            {rows.map(r => {
              const pains = (r.qualification?.pain_points) || (r.lead_brief?.summary_lines || []).filter(l => /pain/i.test(l));
              const isOpen = expanded === r.id;
              return (
                <React.Fragment key={r.id}>
                  <TableRow data-testid={`internal-escalation-row-${r.id}`}>
                    <TableCell>
                      <div className="font-medium">{r.name || "—"}</div>
                      <div className="text-[11px] text-muted-foreground">{r.email}</div>
                    </TableCell>
                    <TableCell><div className="font-medium text-sm">{r.company_name || "—"}</div></TableCell>
                    <TableCell><Badge className={cn("text-[10px]", TEMP_BADGE[r.buyer_temperature] || "")}>{r.buyer_temperature || "—"}</Badge></TableCell>
                    <TableCell><div className="font-mono text-sm">{r.lead_priority_score ?? 0}</div></TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{r.escalation_reason || "—"}</Badge></TableCell>
                    <TableCell className="text-[11px] text-muted-foreground max-w-[180px] truncate">{Array.isArray(pains) ? pains.join(", ") : "—"}</TableCell>
                    <TableCell><Badge className={cn("text-[10px]", EMAIL_BADGE[r.escalation_email_status] || "bg-slate-100 text-slate-500")}>{r.escalation_email_status || "pending"}</Badge></TableCell>
                    <TableCell><Badge className={cn("text-[10px]", QSTATUS_BADGE[r.escalation_status] || "")}>{r.escalation_status || "new"}</Badge></TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => setExpanded(isOpen ? null : r.id)} data-testid={`internal-escalation-toggle-${r.id}`}>
                        {isOpen ? "Hide" : "Open"}
                      </Button>
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow data-testid={`internal-escalation-detail-${r.id}`}>
                      <TableCell colSpan={9} className="bg-muted/30 p-5">
                        <div className="grid md:grid-cols-2 gap-5 text-xs">
                          <div>
                            <div className="font-semibold mb-1 text-foreground">Lead brief</div>
                            <div className="font-medium mb-1">{r.lead_brief?.headline}</div>
                            <ul className="list-disc ml-5 space-y-0.5 text-muted-foreground">
                              {(r.lead_brief?.summary_lines || []).map((l, i) => <li key={i}>{l}</li>)}
                            </ul>
                            <div className="mt-2 text-foreground"><span className="text-muted-foreground">Next step:</span> {r.lead_brief?.recommended_next_step}</div>
                            <div className="mt-1 text-foreground"><span className="text-muted-foreground">Angle:</span> {r.lead_brief?.followup_angle}</div>
                          </div>
                          <div>
                            <div className="font-semibold mb-1 text-foreground">Handoff summary</div>
                            <div className="text-muted-foreground">Operational: {(r.handoff_summary?.operational_concerns || []).join(", ") || "—"}</div>
                            <div className="text-muted-foreground">Implementation: {(r.handoff_summary?.implementation_interests || []).join(", ") || "—"}</div>
                            <div className="text-muted-foreground">Objections: {(r.handoff_summary?.objections_raised || []).join(", ") || "—"}</div>
                            <div className="text-muted-foreground">Unresolved: {r.handoff_summary?.unresolved_question || "—"}</div>

                            <div className="font-semibold mt-3 mb-1 text-foreground">Recommended follow-up</div>
                            <div className="text-muted-foreground">Channel: {r.recommended_followup?.channel} · Speed: {r.recommended_followup?.outreach_speed}</div>
                            <div className="text-muted-foreground">Angle: {r.recommended_followup?.angle}</div>
                            <div className="text-muted-foreground">Playbook: {r.recommended_followup?.playbook}</div>
                          </div>
                        </div>

                        <div className="mt-4 grid md:grid-cols-2 gap-5">
                          <div>
                            <div className="font-semibold mb-1 text-xs">Internal notes</div>
                            <div className="space-y-1 mb-2 max-h-32 overflow-y-auto" data-testid={`internal-escalation-notes-${r.id}`}>
                              {(r.escalation_notes || []).length === 0 && <div className="text-[11px] text-muted-foreground italic">No notes yet.</div>}
                              {(r.escalation_notes || []).map(n => (
                                <div key={n.id} className="text-[11px] border-l-2 border-muted pl-2">
                                  <div>{n.text}</div>
                                  <div className="text-[10px] text-muted-foreground">{n.author} · {(n.created_at || "").slice(0,16).replace("T"," ")}{n.linked_status ? ` · ${n.linked_status}`:""}</div>
                                </div>
                              ))}
                            </div>
                            <Input
                              placeholder="Add internal note…"
                              value={noteDraft[r.id] || ""}
                              onChange={(e) => setNoteDraft(prev => ({ ...prev, [r.id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNote(r.id); } }}
                              data-testid={`internal-escalation-note-input-${r.id}`}
                            />
                          </div>
                          <div className="flex flex-col gap-2 items-start">
                            <div className="text-xs text-muted-foreground">
                              {r.escalation_email_status === "not_configured" && "Email: SendGrid not configured — escalation logged only."}
                              {r.escalation_email_status === "sent" && `Email: delivered ${(r.escalation_email_sent_at||"").slice(0,16).replace("T"," ")}`}
                              {r.escalation_email_status === "failed" && `Email: failed — ${r.escalation_email_error || "unknown"}`}
                              {r.escalation_email_status === "no_recipient" && "Email: no notify_email configured on the tenant."}
                              {!r.escalation_email_status && "Email: pending."}
                            </div>
                            <div className="flex gap-2 flex-wrap">
                              <Button size="sm" variant="outline" onClick={() => updateStatus(r.id, "acknowledged")} data-testid={`internal-escalation-ack-${r.id}`}>Acknowledge</Button>
                              <Button size="sm" variant="outline" onClick={() => updateStatus(r.id, "resolved")} data-testid={`internal-escalation-resolve-${r.id}`}>Resolved</Button>
                              <Button size="sm" variant="ghost" onClick={() => updateStatus(r.id, "dismissed")} data-testid={`internal-escalation-dismiss-${r.id}`}>Dismiss</Button>
                              {r.chatbot_slug && r.session_id && (
                                <Button size="sm" variant="ghost" asChild data-testid={`internal-escalation-open-conv-${r.id}`}>
                                  <a href={`/chat/${r.chatbot_slug}?session=${r.session_id}`} target="_blank" rel="noreferrer">Open conversation ↗</a>
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </InternalAdminLayout>
  );
}


// ============== INTERNAL TEST CHECKLIST (Workflow Readiness v1) ==============
export function InternalTestChecklist() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await api.get("/internal-admin/test-checklist");
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load checklist");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  return (
    <InternalAdminLayout>
      <div className="flex items-start justify-between mb-2">
        <h1 className="font-display text-4xl font-semibold" data-testid="internal-checklist-title">Workflow test checklist</h1>
        <Button size="sm" variant="outline" onClick={fetchData} disabled={loading} data-testid="internal-checklist-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}<span className="ml-2">Refresh</span>
        </Button>
      </div>
      <p className="text-muted-foreground mb-6">End-to-end workflow health for the prospect → qualification → escalation → follow-up pipeline. Counts derived from live data.</p>

      {data && (
        <>
          <div className="flex items-center gap-3 mb-5">
            <div className="font-display text-3xl font-semibold" data-testid="internal-checklist-score">{data.passed} / {data.total}</div>
            <div className="text-sm text-muted-foreground">checks passing</div>
            <Badge variant="outline" className="ml-2 text-[10px]">email_configured: {String(data.email_configured)}</Badge>
            <Badge variant="outline" className="text-[10px]">{new Date(data.computed_at).toLocaleString()}</Badge>
          </div>
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-16"></TableHead>
                <TableHead>Check</TableHead>
                <TableHead>Detail</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.items.map(it => (
                  <TableRow key={it.id} data-testid={`internal-checklist-row-${it.id}`}>
                    <TableCell>
                      {it.pass
                        ? <CheckCircle2 className="w-5 h-5 text-emerald-600" data-testid={`internal-checklist-pass-${it.id}`}/>
                        : <XCircle className="w-5 h-5 text-rose-500" data-testid={`internal-checklist-fail-${it.id}`}/>}
                    </TableCell>
                    <TableCell><div className="font-medium text-sm">{it.label}</div></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{it.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </InternalAdminLayout>
  );
}
